/**
 * @author        nico@zumper.com (Nico Rattazzi)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   Search UI Reducer
 */

import { LISTABLES_API_LIMIT, LISTABLES_API_LIMIT_MAX } from '@zcode/nr/es/shared/constants/list'
import { filterActions } from '@zumper/redux-ignore'

import {
  GET_LISTABLES_REQUEST,
  GET_LISTABLES_SUCCESS,
  SET_LISTABLES_LIMIT,
  SET_LISTABLES_OFFSET,
  SET_LISTABLES_PAGE_OFFSET,
  INCREMENT_LOAD_MORE_COUNT,
  UPDATE_SEARCH_UI,
  SET_HOVERED_ITEM,
  SET_HOVERED_CLUSTER_ITEM,
  GET_LISTABLES_COUNTS_BY_REQUEST,
  GET_LISTABLES_COUNTS_BY_SUCCESS,
  GET_LISTABLES_COUNTS_BY_ERROR,
  SET_FILTERS_VIEW_START_POINT,
  SET_IS_FILTERS_VIEW,
  CLEAR_FILTERS_VIEW_START_POINT,
  SEARCH_VIEW_TYPE,
} from '../actions/constants'
import {
  SHOW as SHOW_FAVORITES_VIEW,
  HIDE as HIDE_FAVORITES_VIEW,
} from '../modules/favoritesView/constants/index'
import {
  SHOW as SHOW_MESSAGED_VIEW,
  HIDE as HIDE_MESSAGED_VIEW,
} from '../modules/messagedView/constants/index'
import {
  SHOW as SHOW_SAVED_SEARCHES_VIEW,
  HIDE as HIDE_SAVED_SEARCHES_VIEW,
} from '../modules/savedSearchesView/constants/index'

const whitelist = [
  GET_LISTABLES_REQUEST,
  GET_LISTABLES_SUCCESS,
  SET_LISTABLES_LIMIT,
  SET_LISTABLES_OFFSET,
  SET_LISTABLES_PAGE_OFFSET,
  INCREMENT_LOAD_MORE_COUNT,
  UPDATE_SEARCH_UI,
  SET_HOVERED_ITEM,
  SET_HOVERED_CLUSTER_ITEM,
  GET_LISTABLES_COUNTS_BY_REQUEST,
  GET_LISTABLES_COUNTS_BY_SUCCESS,
  GET_LISTABLES_COUNTS_BY_ERROR,
  SET_FILTERS_VIEW_START_POINT,
  SET_IS_FILTERS_VIEW,
  CLEAR_FILTERS_VIEW_START_POINT,
  HIDE_SAVED_SEARCHES_VIEW,
  SHOW_SAVED_SEARCHES_VIEW,
  HIDE_FAVORITES_VIEW,
  SHOW_FAVORITES_VIEW,
  HIDE_MESSAGED_VIEW,
  SHOW_MESSAGED_VIEW,
]
const searchUIReducer = function searchUI(
  state = {
    isCountsByFetching: false,
    isFiltersView: false,
    isNavbarSearchFocused: false,
    isClustersFetching: false,
    limit: LISTABLES_API_LIMIT,
    loadMoreCount: 0,
    offset: 0,
    pageOffset: 0,
    searchView: SEARCH_VIEW_TYPE.MAIN,
  },
  action
) {
  switch (action.type) {
    // LISTABLES TODO fix this by moving to its own module
    case GET_LISTABLES_REQUEST: {
      return {
        ...state,
        isListablesFetching: true,
      }
    }
    case GET_LISTABLES_SUCCESS: {
      // continuation search, merge existing listables + new listables
      const listablesSize = action.listables.listables.length
      const featuredSize = (action.listables.featured && action.listables.featured.length) || 0
      const { matching } = action.listables

      // NOTE: getListables has been upgraded to use offset instead of reset.
      // The other queries for featured, messaged and saved searches still use
      // reset. Using offset allows us to infer that the results are a next
      // page while reset is used to specify that we're receiving a first page.
      const { offset, excludeGroupIds, reset } = action

      // more result available
      // TODO: does this actually work?! Seems like the listables in a specific
      // page would usually add up to less than the matching?
      const hasMore = !!(listablesSize || listablesSize + featuredSize < matching)

      // TODO: we use the exact same key to track the status of 5 separate types
      // of queries (yikes)! We should keep the statuses for currentSearch,
      // favorites, messaged and savedSearches in 5 different sub-keys of
      // searchUi (or in currentSearch where they are used)
      let nextState = {
        ...state,
        isListablesFetching: false,
        moreResult: hasMore,
      }

      // capture the offset
      if (offset) {
        nextState = {
          ...nextState,
          offset,
        }
      } else if ((!offset && !excludeGroupIds) || reset) {
        // for fresh results, reset the limit and loadMoreCount
        nextState = {
          ...nextState,
          limit: LISTABLES_API_LIMIT,
          loadMoreCount: 0,
          offset: 0,
        }
      }
      return nextState
    }
    case SET_LISTABLES_LIMIT: {
      const limit = action.payload
      let value = limit
      if (limit < LISTABLES_API_LIMIT) {
        value = LISTABLES_API_LIMIT
      } else if (limit > LISTABLES_API_LIMIT_MAX) {
        value = LISTABLES_API_LIMIT_MAX
      }
      return {
        ...state,
        limit: value,
      }
    }
    case SET_LISTABLES_OFFSET: {
      const offset = action.payload
      const value = offset > 0 ? offset : 0
      // TODO: check that offset is less than matching
      return {
        ...state,
        offset: value,
      }
    }
    case SET_LISTABLES_PAGE_OFFSET: {
      const offset = action.payload
      const value = offset > 0 ? offset : 0
      return {
        ...state,
        pageOffset: value,
      }
    }
    case INCREMENT_LOAD_MORE_COUNT: {
      const count = (state?.loadMoreCount ?? 0) + 1
      return {
        ...state,
        loadMoreCount: count,
      }
    }

    case UPDATE_SEARCH_UI:
      if (action.key) {
        return {
          ...state,
          [action.key]: action.value,
        }
      }
      return state
    case SHOW_FAVORITES_VIEW: {
      return {
        ...state,
        searchView: SEARCH_VIEW_TYPE.FAVORITES,
      }
    }
    case SHOW_MESSAGED_VIEW: {
      return {
        ...state,
        searchView: SEARCH_VIEW_TYPE.MESSAGED,
      }
    }
    case SHOW_SAVED_SEARCHES_VIEW: {
      return {
        ...state,
        searchView: SEARCH_VIEW_TYPE.SEARCHES,
      }
    }
    case SET_IS_FILTERS_VIEW: {
      const { payload } = action
      return {
        ...state,
        isFiltersView: payload,
      }
    }
    case HIDE_FAVORITES_VIEW:
    case HIDE_MESSAGED_VIEW:
    case HIDE_SAVED_SEARCHES_VIEW: {
      const { payload } = action
      if (payload && payload.viewType) {
        return {
          ...state,
          searchView: payload.viewType,
        }
      }
      return {
        ...state,
        searchView: SEARCH_VIEW_TYPE.MAIN,
      }
    }
    case SET_HOVERED_ITEM: {
      return {
        ...state,
        hoveredItem: action.item,
      }
    }
    case SET_HOVERED_CLUSTER_ITEM: {
      return {
        ...state,
        hoveredClusterItem: action.payload,
      }
    }
    case GET_LISTABLES_COUNTS_BY_REQUEST:
      return {
        ...state,
        isCountsByFetching: true,
      }
    case GET_LISTABLES_COUNTS_BY_SUCCESS:
    case GET_LISTABLES_COUNTS_BY_ERROR:
      return {
        ...state,
        isCountsByFetching: false,
      }
    case SET_FILTERS_VIEW_START_POINT: {
      const { payload } = action
      return {
        ...state,
        filtersViewStartPoint: payload,
      }
    }
    case CLEAR_FILTERS_VIEW_START_POINT:
      return {
        ...state,
        filtersViewStartPoint: undefined,
      }
    default:
      return state
  }
}

export default filterActions(searchUIReducer, whitelist)
